import { CopyOutlined, MessageOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Divider, Drawer, Space } from 'antd';
import copy from 'copy-to-clipboard';
import dayjs from 'dayjs';
import { useWorkMutation } from 'hooks';
import { IKeys, IWork, IWorkState } from 'models';
import { useRef, useState } from 'react';
import { SlotInfo } from 'react-big-calendar';
import { workService } from 'services';
import { copyOrderInfo, normalizePhone } from 'utils';
import { getWorkNumber } from 'utils/get-work-number';

import { CreateWorkButtons } from './CreateWorkButtons';
import { FollowUps } from './FollowUps';
import { CreateWorkForm, CreateWorkFormRef } from './forms';
import { WorkHistory } from './WorkHistory';
import { WorkTitle } from './WorkTitle';

interface MyModalProps {
	work: IWork | null;
	slot?: SlotInfo | null;
	handleCancel: () => void;
	queryKey: any[];
	open?: boolean;
	refetch?: () => void;
}

export const MyModal = ({
	work,
	handleCancel,
	slot,
	queryKey,
	open,
	refetch
}: MyModalProps) => {
	const [history, openHistory] = useState(false);
	const [followups, openFollowups] = useState(false);
	const formRef = useRef<CreateWorkFormRef>(null);
	const { updateWork, deleteWork, messageApi, contextHolder } = useWorkMutation(
		{
			queryKey,
			work,
			onSettled: () => handleCancel(),
			onSettledDelete: () => handleCancel()
		}
	);

	const { data, isLoading } = useQuery({
		queryFn: () => workService.getOne(work?.id || 0),
		queryKey: [IKeys.WORKS, { id: work?.id }],
		enabled: !!work?.id,
		gcTime: 0,
		staleTime: 0
	});

	const onFinish = (values: Partial<IWork>) => {
		if (values.acf) {
			values.acf.date = dayjs(values.acf.date).format('YYYYMMDD');
			values.acf.customer_info.customer_phone = normalizePhone(values.acf.customer_info.customer_phone);
			values.acf.deposit = values.acf.deposit?.toString();
			updateWork.mutate(values);
		}
	};

	const onDeleteJob = () => {
		deleteWork.mutate();
	};

	const lostWork = async () => {
		await updateWork.mutateAsync({
			acf: {
				state: IWorkState.LOST
			}
		});
		refetch?.();
	};

	const submitForm = () => {
		formRef.current?.submitForm();
	}; 

	const copyToClickBoard = () => {
		if(work) {
			copy(copyOrderInfo(work));
			messageApi.success('Copied!');
		}
	};

	return (
		<Drawer
			destroyOnHidden
			title={data ? <WorkTitle work={data} /> : slot ? 'Add new work' : 'Create a quote'}
			size="large"
			loading={isLoading}
			open={!!work || !!slot || open}
			onClose={handleCancel}
			footer={<CreateWorkButtons
				submitForm={submitForm} 
				lostWork={lostWork}
				onDeleteJob={onDeleteJob}
				state={data?.acf.state}
				handleCancel={handleCancel}
				loading={deleteWork.isPending || updateWork.isPending}
				openHistory={() => openHistory(true)}
			/>}
		>
			{data && (
				<Space.Compact block>
					<Button
						onClick={copyToClickBoard}
						type="primary"
						size="large"
						icon={<CopyOutlined />}
						block
					>
						Copy work
					</Button>
					<Button
						onClick={() => openFollowups(true)}
						danger
						type="primary"
						size="large"
						icon={<MessageOutlined />}
						block
					>
						Follow Ups {data.acf.followups?.length}
					</Button>
				</Space.Compact>
			)}
			<Divider size="small" />
			<CreateWorkForm
				work={data}
				onFinish={onFinish}
				slot={slot}
				queryKey={queryKey}
				ref={formRef}
			/>
			{contextHolder}
			{data?.id  && (
				<>
					<Drawer
						title={`Work ${getWorkNumber(data)} history`}
						size="large"
						onClose={() => openHistory(false)}
						open={history}
						destroyOnHidden
					>
						<WorkHistory workId={data.id}/>
					</Drawer>
					<Drawer
						title="Follow ups"
						size="large"
						onClose={() => openFollowups(false)}
						open={followups}
						destroyOnHidden
					>
						<FollowUps
							workId={data.id}
							customer_phone={normalizePhone(data.acf.customer_info.customer_phone)}
						/>
					</Drawer>
				</>
			)}
		</Drawer>
	);
};
